import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { V2LoadingSpinner as LoadingSpinner } from '@abyss/web/ui/LoadingSpinner';
import { Popover } from '@abyss/web/ui/Popover';
import { Skeleton } from '@abyss/web/ui/Skeleton';
import find from 'lodash/find';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useGetRecommendationPreferencesStatus } from '../../../hooks/useGetRecommendationPreferencesStatus';
import { useHsidUuid } from '../../../hooks/useHsidUuid';
import { useLagoon } from '../../../hooks/useLagoon';
import { useProviderDetailsWidget } from '../../../hooks/useProviderDetailsWidget';
import { useSaveRecommendationPreferences } from '../../../hooks/useSaveRecommendationPreferencesStatus';
import { useSmartChoice } from '../../../hooks/useSmartChoice';
import { useSmartChoiceSessionStorage } from '../../../hooks/useSmartChoiceSessionStorage';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { useWidgetSmartChoice } from '../../../hooks/useWidgetSmartChoice';
import { ProviderType } from '../../../models/ProviderDetails';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import { shouldSuppressVirtualOnlyLocationIndicator } from '../../../utils/indicator.utils';
import {
  SMART_CHOICE_EVENTS,
  dispatchSmartChoiceEvent,
} from '../../../utils/smartChoiceEvents';
import { getCurrentMember } from '../../../utils/user.utils';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../AdobeTagging/adobeModalTrackEvent';
import {
  CareCategories,
  Constants,
  SHOULD_GET_HG_DATA,
  SectionType,
} from '../../Constants';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { mobileOnly } from '../../ConstantsStyles';
import { SmartChoiceDetails } from '../../DataCard/SmartChoiceDetails';
import { LinkContainer } from '../../LinkContainer/LinkContainer';
import {
  coachmarkStyle,
  smartChoiceLinkStyle,
} from '../../PSXHeader/PSXHeader.styled';
import { isValidCareCategoryWithSectionType } from '../../Utils/getProviderType';
import { IntroModal } from './IntroModal';
import { NonSmartChoiceOnboarding } from './NonSmartChoiceOnboarding';
import PopoverContent from './PopoverContent';
import { determineIsSuperUser } from './superUserUtils';
import { formatPreferencePayload } from './utils';
import { OnboardingForm, PreferencesForm } from '.';

type Props = {
  breadcrumbs?: any[];
  isMixedResultsPage?: boolean;
  showMemberSelection?: boolean;
  showChoosePCPHeader?: boolean;
  isFullScreenCompareSmartChoice?: boolean;
  flexCss?: any;
  isParcelWidget?: boolean;
  isSuperUser?: boolean | string;
  showDetailsPage?: boolean;
  memberId?: string;
  submitButtonLabel?: string;
  providerId?: string;
  locationId?: string;
  providerType?: ProviderType;
  latitude?: string;
  longitude?: string;
  reciprocityId?: string;
  coverageType?: string;
  claimSystemTypeCode?: string;
  eligibilitySystemTypeCode?: string;
  planVariationCode?: string;
  planYear?: string;
  policyId?: string;
  lob?: string;
  membershipCategory?: string;
  population?: string;
  isSmartChoiceEnabled?: boolean;
  billingCodes?: string;
  cesProductCode?: string;
  memberMarketType?: string;
};

export const SmartChoiceWidget = ({
  isFullScreenCompareSmartChoice = false,
  flexCss,
  isParcelWidget = false,
  isSuperUser: isSuperUserProp = false,
  showDetailsPage = false,
  memberId,
  submitButtonLabel,
  providerId,
  locationId,
  providerType,
  latitude,
  longitude,
  reciprocityId,
  coverageType,
  claimSystemTypeCode,
  eligibilitySystemTypeCode,
  planVariationCode,
  planYear,
  policyId,
  lob,
  membershipCategory,
  population,
  isSmartChoiceEnabled,
  billingCodes,
  cesProductCode,
  memberMarketType,
}: Props) => {
  const { t } = useTranslation();
  const realHsidUuid = useHsidUuid();

  // Use memberId prop if provided, otherwise fallback to realHsidUuid
  const effectiveUserId = memberId || realHsidUuid;
  const allConfigs = useLagoon('config');

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('mapPinRedesign') === 'true') {
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.FORCE_MAP_PIN_REDESIGN,
      true
    );
  }

  // features
  const [enableSmartChoiceIntroPopup] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SMART_CHOICE_INTRO_POPUP,
  ]);
  const [enableRRVirtualCareCEXWidget] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_RR_VIRTUAL_CARE_CEX_WIDGET,
  ]);
  const enableNonSmartChoiceDisclaimer = useFeatureFlag([
    ConstantsLagoon?.FEATURE_FLAGS.ENABLE_NON_SMART_CHOICE_DISCLAIMER,
  ])[0];
  const [enablePopoverPriorityFlow] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_POPOVER_PRIORITY_FLOW,
  ]);
  const featureSuppressionData = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_SUPPRESSION
  )();

  const isMobileScreen = useMediaQuery(mobileOnly);

  const [showOnboardingForm, setShowOnboardingForm] = useState(false);
  const [showNonSmartChoiceOnboarding, setShowNonSmartChoiceOnboarding] =
    useState(false);
  const [isPreferencesModalOpen, setPreferencesModalOpen] =
    React.useState(false);
  const { sectionType, search } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
      sectionType: state.typeaheadSearchStore.sectionType, // Extract sectionType
      search: state.typeaheadSearchStore.search, // Extract search
    }))
  );

  const lagoonData = useLagoon('ui-messaging')();

  const SC_COACHMARK = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.SC_COACHMARK,
  });
  const SMART_CHOICE_INTRO_POP_UP_SUBTITLE = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_SUBTITLE,
  });
  const SMART_CHOICE_INTRO_POP_UP_LEARN_ABOUT = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_LEARN_ABOUT,
  });
  const SMART_CHOICE_INTRO_POP_UP_MAY_BE_LATER = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_MAY_BE_LATER,
  });
  const locationAnalytics = `modal:${SMART_CHOICE_INTRO_POP_UP_SUBTITLE?.message}`;

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue } = chipStore;

  const [coachmarkWeb, setCoachmarkWebOpen] = useState<boolean>(false);
  const isCoachmarkShownThisSession = useRef(false);

  const recommendationPreferencesStatusFromStore = useStore(
    StoreKeys.RECOMMENDATION_PREFERENCES
  );

  const setRecommendationPreferences = useStore(
    StoreKeys.SET_RECOMMENDATION_PREFERENCES
  );
  const [showSmartchoiceIntro, setShowSmartchoiceIntro] = useState(false);
  const [showCoachMarkCondition, setShowCoachMarkCondition] = useState(false);
  // State to track if sessionStorage has been initialized with preferences
  // Check on mount if sessionStorage already has values from a previous page
  const [isSessionStorageReady, setIsSessionStorageReady] = useState<
    boolean | undefined
  >(() => {
    const hasDismissedPopover = sessionStorage.getItem('hasDismissedPopover');
    return hasDismissedPopover !== null;
  });

  // Determine isSuperUser with proper order of operations and fallback
  // Priority 0: Use prop from parent (e.g., CEX) if provided
  // Priority 1: Guest check
  // Priority 2: Session storage
  // Priority 3: Lagoon config
  // Priority 4: Fallback to false
  const isSuperUser = determineIsSuperUser(allConfigs, isSuperUserProp);

  const [preferencesData, getPreferenceData] =
    useGetRecommendationPreferencesStatus({});

  useEffect(() => {
    if (!recommendationPreferencesStatusFromStore && isParcelWidget) {
      getPreferenceData({
        variables: {
          ...(effectiveUserId && { userId: effectiveUserId }),
          isSuperUser,
        },
      });
    }
  }, [isSuperUser, isParcelWidget, effectiveUserId]);

  const recommendationPreferencesStatus = useMemo(() => {
    const apiData = preferencesData?.data?.recommendationPreferencesStatus;
    const storeData = recommendationPreferencesStatusFromStore;

    // If we have store data with a lastUpdatedDate, compare to determine which is newer
    // Store data is updated with save responses, so it may be more recent than the initial API fetch
    if (storeData?.lastUpdatedDate && apiData?.lastUpdatedDate) {
      const storeDate = new Date(storeData.lastUpdatedDate).getTime();
      const apiDate = new Date(apiData.lastUpdatedDate).getTime();
      return storeDate >= apiDate ? storeData : apiData;
    }

    // If only store has lastUpdatedDate (after a save), prefer store data
    if (storeData?.lastUpdatedDate) {
      return storeData;
    }

    // Otherwise, prefer API data (initial load) or fallback to store
    return apiData || storeData;
  }, [
    preferencesData?.data?.recommendationPreferencesStatus,
    recommendationPreferencesStatusFromStore,
  ]);

  // Use custom hook to set sessionStorage values before any render that depends on them
  useSmartChoiceSessionStorage({
    preferencesData,
    isSuperUser,
    enableSmartChoiceIntroPopup,
    setRecommendationPreferences,
    setIsSessionStorageReady,
    disabled: !isParcelWidget,
  });

  const [saveResult, saveRecommendationPreferences] =
    useSaveRecommendationPreferences({
      handleSuccess: () => {
        // Refetch preferences after save - this is required for the backend to properly persist
        // the data. The save response alone is not sufficient; the subsequent GET call
        // finalizes the save operation.
        getPreferenceData({
          variables: {
            ...(effectiveUserId && { userId: effectiveUserId }),
            isSuperUser,
          },
        });
        setPreferencesModalOpen(false);
      },
      handleError: (error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to save recommendation preferences:', error);
        // Close the modal even on error to prevent UI getting stuck
        setPreferencesModalOpen(false);
      },
    });

  // getting provider data for the smart choice details component
  const [showSmartChoiceDetailsDrawer, setShowSmartChoiceDetailsDrawer] =
    useState(showDetailsPage);
  const shouldGetHGData = SHOULD_GET_HG_DATA;
  const currentMember = getCurrentMember();
  const virtualOnlyLocationIndFlagKey =
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS
      .VIRTUAL_ONLY_LOCATION_INDICATOR_UNET_RAILROAD;
  const virtualOnlyLocationInd =
    enableRRVirtualCareCEXWidget &&
    shouldSuppressVirtualOnlyLocationIndicator(
      currentMember?.policyNumber,
      true, // "Set hasBH to true as part of the tactical approach approved by Julie Chaloner"
      virtualOnlyLocationIndFlagKey,
      featureSuppressionData
    );
  const isWidgetSmartChoiceEnabled =
    isSmartChoiceEnabled && coverageType
      ? useWidgetSmartChoice(isSmartChoiceEnabled, coverageType)
      : true;

  const providerResponse = useProviderDetailsWidget({
    claimSystemTypeCode,
    coverageType,
    eligibilitySystemTypeCode,
    latitude,
    lob,
    locationId,
    longitude,
    planVariationCode,
    planYear,
    policyId,
    providerId,
    providerType,
    reciprocityId,
    shouldGetHGData,
    membershipCategory,
    population,
    virtualOnlyLocationInd,
    isWidgetSmartChoiceEnabled,
    billingCodes,
    cesProductCode,
    memberMarketType,
  });
  const isTieredPlan = providerResponse?.data.providerDetails?.isTieredPlan;
  const allProviderLocations =
    providerResponse?.data.providerDetails?.providerDetails?.providerLocations;
  const providerLocation = allProviderLocations
    ? allProviderLocations.find((item) => item.locationId === locationId)
    : null;

  const handleCloseDetailsDrawer = () => {
    dispatchSmartChoiceEvent(SMART_CHOICE_EVENTS.DETAILS_CLOSED, {
      operation: 'close-drawer',
      timestamp: new Date().toISOString(),
    });
    setShowSmartChoiceDetailsDrawer(false);
  };

  useEffect(() => {
    const savedPreferences =
      saveResult?.data?.saveRecommendationPreferencesStatus;
    if (savedPreferences) {
      setRecommendationPreferences(savedPreferences);

      // Dispatch event for parent apps (e.g., CEX) to update their Redux store
      if (isParcelWidget) {
        dispatchSmartChoiceEvent(SMART_CHOICE_EVENTS.PREFERENCES_SAVED, {
          preferences: savedPreferences,
          source: 'api-save',
          timestamp: new Date().toISOString(),
          userId: effectiveUserId,
          isSuperUser,
        });
      }
    }
  }, [
    saveResult?.data?.saveRecommendationPreferencesStatus,
    isParcelWidget,
    effectiveUserId,
    isSuperUser,
    setRecommendationPreferences,
  ]);

  const isSmartChoiceWithChipValue = useSmartChoice();
  const isSmartChoiceFFEnabled = useSmartChoice(true);

  const isSmartChoice =
    chipValue === CareCategories.ALL
      ? isSmartChoiceFFEnabled
      : isSmartChoiceWithChipValue;

  const shouldDisplayCoachMarkBasedOnChipValue = () =>
    (chipValue === CareCategories.ALL ||
      chipValue === CareCategories.PRIMARY_CARE ||
      chipValue === CareCategories.MEDICAL_SPECIALISTS) &&
    sectionType !== SectionType.PROVIDER_GROUP;

  // Initialize showSmartchoiceIntro and showCoachMarkCondition once sessionStorage is ready
  useEffect(() => {
    if (recommendationPreferencesStatus) {
      const hasDismissedPopover = sessionStorage.getItem('hasDismissedPopover');
      const smartChoicePopoverDismissed = sessionStorage.getItem(
        'smartChoicePopoverDismissed'
      );
      const shouldShowIntro =
        hasDismissedPopover === 'n' && smartChoicePopoverDismissed !== 'true';
      setShowSmartchoiceIntro(shouldShowIntro);

      // Compute coachmark condition
      const hasDismissedCoachMarkWeb =
        recommendationPreferencesStatus?.hasDismissedCoachMarkWeb;
      let shouldShowCoachMark = false;

      if (enableSmartChoiceIntroPopup) {
        // If popover hasn't been dismissed (hasDismissedPopover === 'n'), don't show coachmark
        if (hasDismissedPopover === 'n') {
          shouldShowCoachMark = false;
        }
        // If popover has been dismissed (hasDismissedPopover === 'y'), coachmark becomes independent
        else if (hasDismissedPopover === 'y') {
          shouldShowCoachMark = hasDismissedCoachMarkWeb !== 'y';
        }
      } else {
        shouldShowCoachMark = hasDismissedCoachMarkWeb !== 'y';
      }

      // Don't flip to false if coachmark is actively shown this session —
      // the DB is already updated; closing only happens on explicit user action
      // or navigation (component remount resets the ref).
      if (
        enablePopoverPriorityFlow &&
        !shouldShowCoachMark &&
        isCoachmarkShownThisSession.current
      ) {
        return;
      }
      setShowCoachMarkCondition(shouldShowCoachMark);
    }
  }, [
    isSessionStorageReady,
    enableSmartChoiceIntroPopup,
    recommendationPreferencesStatus,
  ]);

  useEffect(() => {
    if (isSmartChoice && recommendationPreferencesStatus) {
      setRecommendationPreferences(recommendationPreferencesStatus);
    }
  }, [recommendationPreferencesStatus, isSmartChoice]);

  // Dispatch event when preferences are loaded from API (for parent app integration)
  useEffect(() => {
    if (
      isParcelWidget &&
      preferencesData?.data?.recommendationPreferencesStatus
    ) {
      dispatchSmartChoiceEvent(SMART_CHOICE_EVENTS.PREFERENCES_LOADED, {
        preferences: preferencesData.data.recommendationPreferencesStatus,
        source: 'api-fetch',
        timestamp: new Date().toISOString(),
        userId: effectiveUserId,
        isSuperUser,
      });
    }
  }, [
    preferencesData?.data?.recommendationPreferencesStatus,
    isParcelWidget,
    effectiveUserId,
    isSuperUser,
  ]);

  const onClickSaveAndUpdate = (data) => {
    // Build uuid with superUser suffix if applicable (matches expected API format)
    const uuidWithSuffix = effectiveUserId
      ? `${effectiveUserId}${isSuperUser ? '-superUser' : ''}`
      : undefined;

    saveRecommendationPreferences({
      variables: {
        ...data,
        ...(uuidWithSuffix && { uuid: uuidWithSuffix }),
        ...(effectiveUserId && { userId: effectiveUserId }),
      },
    });
    // Note: The save response will be handled by the useEffect that watches saveResult
    // This updates the Zustand store and dispatches events for parent apps
  };

  const updatePreferencesForFirstTimeUser = () => {
    const payload = formatPreferencePayload({
      ...recommendationPreferencesStatus,
      hasDismissedCoachMarkWeb: 'y',
      isSuperUser,
    });

    onClickSaveAndUpdate(payload);
  };
  const ENABLE_NON_SC_DISCLAIMER = enableNonSmartChoiceDisclaimer
    ? isSmartChoiceFFEnabled
    : isSmartChoice;

  const isValidCategoryWithSectionType = isValidCareCategoryWithSectionType(
    chipValue,
    sectionType
  );

  const onClickSmartChoiceHandler = () => {
    const currentChip = chipValue;
    if (
      currentChip === CareCategories?.VIRTUAL_VISIT ||
      currentChip === CareCategories?.ALL ||
      ((currentChip === CareCategories?.PRIMARY_CARE ||
        currentChip === CareCategories?.MEDICAL_SPECIALISTS) &&
        (sectionType?.toLowerCase() === SectionType.PROVIDER.toLowerCase() ||
          sectionType === undefined))
    ) {
      if (recommendationPreferencesStatus?.hasSetPreferences === 'y') {
        setPreferencesModalOpen(true);
        adobeModalTrackEvent('Set preferences');
      } else {
        if (recommendationPreferencesStatus?.hasDismissedCoachMarkWeb !== 'y') {
          updatePreferencesForFirstTimeUser();
        }
        adobeModalTrackEvent('A better way to find providers');
        setShowOnboardingForm(true);
      }
    } else if (enableNonSmartChoiceDisclaimer) {
      setShowNonSmartChoiceOnboarding(true);
    }
  };

  useEffect(() => {
    if (
      recommendationPreferencesStatus?.hasDismissedCoachMarkWeb !== 'y' &&
      showCoachMarkCondition
    ) {
      setCoachmarkWebOpen(true);
      if (enablePopoverPriorityFlow && !isCoachmarkShownThisSession.current) {
        isCoachmarkShownThisSession.current = true;
        updatePreferencesForFirstTimeUser();
      }
    } else if (enablePopoverPriorityFlow) {
      if (!isCoachmarkShownThisSession.current) {
        setCoachmarkWebOpen(false);
      }
    } else {
      setCoachmarkWebOpen(false);
    }
  }, [
    recommendationPreferencesStatus?.hasDismissedCoachMarkWeb,
    setCoachmarkWebOpen,
    showCoachMarkCondition,
  ]);

  useEffect(() => {
    if (enablePopoverPriorityFlow && coachmarkWeb) {
      setCoachmarkWebOpen(false);
    }
  }, [chipValue]);

  useEffect(() => {
    if (coachmarkWeb) {
      setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement) {
          activeElement.blur();
        }
      }, 0);
    }
  }, [coachmarkWeb]);

  const onCloseCoachmarkWeb = () => {
    if (enablePopoverPriorityFlow) {
      isCoachmarkShownThisSession.current = false;
    } else {
      updatePreferencesForFirstTimeUser();
    }
    setCoachmarkWebOpen(false);
    adobeLinkTrackEvent({
      name: 'close',
      location: 'Smart choice preferences coachmark',
    });
  };

  const checkMobileScreen = (mobileVal: string | null, desktopVal: string) =>
    isMobileScreen ? mobileVal : desktopVal;

  const dismissPopover = async (hasDismissedCoachMarkWeb?: boolean) => {
    setShowSmartchoiceIntro(false);
    sessionStorage.setItem('smartChoicePopoverDismissed', 'true');

    const uuidWithSuffix = effectiveUserId
      ? `${effectiveUserId}${isSuperUser ? '-superUser' : ''}`
      : undefined;

    const payload = {
      ...recommendationPreferencesStatus,
      hasDismissedPopover: 'y',
      hasDismissedCoachMarkWeb: hasDismissedCoachMarkWeb === true ? 'y' : 'n',
      isSuperUser,
      ...(uuidWithSuffix && { uuid: uuidWithSuffix }),
      ...(effectiveUserId && { userId: effectiveUserId }),
    };
    try {
      await saveRecommendationPreferences({
        variables: payload,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error dismissing popover:', error);
    }
    adobeLinkTrackEvent({
      name: 'close',
      location: locationAnalytics,
    });
  };

  const handleLearnAboutSC = async () => {
    await dismissPopover(true);
    adobeLinkTrackEvent({
      name: SMART_CHOICE_INTRO_POP_UP_LEARN_ABOUT?.message,
      location: locationAnalytics,
    });
    if (recommendationPreferencesStatus?.hasSetPreferences === 'y') {
      setPreferencesModalOpen(true);
      adobeModalTrackEvent('Set preferences');
    } else {
      setShowOnboardingForm(true);
      adobeModalTrackEvent('A better way to find providers');
    }
  };

  const handleMaybeLater = async () => {
    await dismissPopover();
    adobeLinkTrackEvent({
      name: SMART_CHOICE_INTRO_POP_UP_MAY_BE_LATER?.message,
      location: locationAnalytics,
    });
  };

  const LoadingSkeleton = () => (
    <Skeleton height={20} variant="rounded" width={100} />
  );

  if (
    isParcelWidget &&
    isSessionStorageReady !== undefined &&
    !isSessionStorageReady
  ) {
    return <LoadingSkeleton />;
  } else if (
    providerId &&
    providerResponse?.isLoading &&
    !providerResponse?.error &&
    !(providerLocation && providerLocation.recommendationDetails)
  ) {
    return (
      <LoadingSpinner
        ariaLoadingLabel="Loading Provider Details"
        isLoading={true}
        size="xs"
      />
    );
  }

  const SmartChoiceSettingsPage = () => (
    <React.Fragment>
      {ENABLE_NON_SC_DISCLAIMER && !isFullScreenCompareSmartChoice && (
        <Flex css={flexCss as any}>
          {ENABLE_NON_SC_DISCLAIMER && !recommendationPreferencesStatus && (
            <LoadingSkeleton />
          )}
          {ENABLE_NON_SC_DISCLAIMER &&
            isValidCategoryWithSectionType &&
            recommendationPreferencesStatus && (
              <>
                {showCoachMarkCondition && (
                  <Popover
                    align="center"
                    content={
                      <PopoverContent
                        SC_COACHMARK={SC_COACHMARK}
                        onCloseCoachmarkWeb={onCloseCoachmarkWeb}
                      />
                    }
                    // cast to any because coachmarkStyle returns nested selectors and '!important' values
                    css={
                      {
                        ...{
                          zIndex: '99999',
                          '&.abyss-popover-trigger': {
                            marginTop: '8px',
                          },
                        },
                        ...coachmarkStyle(checkMobileScreen),
                      } as any
                    }
                    data-testid="smart-choice-coachmark-web"
                    open={
                      coachmarkWeb && shouldDisplayCoachMarkBasedOnChipValue()
                    }
                    position={isMobileScreen ? 'left' : 'bottom'}
                    showClose={false}
                    style={{ zIndex: 9999 }}
                    width={450}
                  >
                    <div css={{ display: 'flex', alignItems: 'center' } as any}>
                      <IconSymbol
                        color="$primary1"
                        icon="settings"
                        size={24}
                        variant="outlined"
                      />
                    </div>
                  </Popover>
                )}
                <LinkContainer
                  analyticsInfo={{
                    name: 'smart choice gear icon',
                    location: 'smart choice',
                  }}
                  before={
                    (
                      enablePopoverPriorityFlow
                        ? !showCoachMarkCondition
                        : recommendationPreferencesStatus?.hasDismissedCoachMarkWeb ===
                          'y'
                    ) ? (
                      <IconSymbol
                        color="$primary1"
                        icon="settings"
                        size={24}
                        variant="outlined"
                      />
                    ) : null
                  }
                  css={
                    {
                      ...smartChoiceLinkStyle(isMobileScreen, search),
                    } as any
                  }
                  data-auto-testid="smart-choice-button"
                  data-testid="smart-choice-button"
                  icon={
                    recommendationPreferencesStatus?.hasSetPreferences !== 'y'
                      ? 'circle'
                      : ''
                  }
                  iconSize="18"
                  onClick={() => onClickSmartChoiceHandler()}
                  size={18}
                  text={checkMobileScreen(null, t('SMART_CHOICE.SMART_CHOICE'))}
                />
              </>
            )}
        </Flex>
      )}
      {showNonSmartChoiceOnboarding && (
        <NonSmartChoiceOnboarding
          handleCloseDrawer={() => setShowNonSmartChoiceOnboarding(false)}
          handleOpenSetPreferencesModal={() => setPreferencesModalOpen(true)}
          isOpen={showNonSmartChoiceOnboarding}
        />
      )}
      {isSmartChoiceFFEnabled &&
        enableSmartChoiceIntroPopup &&
        showSmartchoiceIntro && (
          <IntroModal
            handleCloseDrawer={dismissPopover}
            handleLearnAboutSC={handleLearnAboutSC}
            handleMaybeLater={handleMaybeLater}
            isOpen={showSmartchoiceIntro}
          />
        )}
      {showOnboardingForm && (
        <OnboardingForm
          handleCloseDrawer={() => setShowOnboardingForm(false)}
          handleOpenSetPreferencesModal={() => setPreferencesModalOpen(true)}
          isOpen={showOnboardingForm}
        />
      )}
      {isPreferencesModalOpen && (
        <PreferencesForm
          handleCloseDrawer={() => setPreferencesModalOpen(false)}
          isOpen={isPreferencesModalOpen}
          isSuperUser={isSuperUser}
          onClickSaveAndUpdate={onClickSaveAndUpdate}
          preferencesData={recommendationPreferencesStatus}
          preferencesFormFromProviderDetail={false}
          submitButtonLabel={submitButtonLabel}
        />
      )}
    </React.Fragment>
  );

  const SmartChoiceDetailsPage = () =>
    providerLocation && providerLocation.recommendationDetails ? (
      <SmartChoiceDetails
        handleCloseDrawer={handleCloseDetailsDrawer}
        isOpen={showSmartChoiceDetailsDrawer}
        isTieredPlan={isTieredPlan}
        preferencesData={recommendationPreferencesStatusFromStore}
        providerType={providerType}
        recommendationDetails={providerLocation.recommendationDetails}
        selectedChip={chipValue}
        stateCode={providerLocation.address?.state}
      />
    ) : null;

  return (
    <React.Fragment>
      {showSmartChoiceDetailsDrawer
        ? SmartChoiceDetailsPage()
        : SmartChoiceSettingsPage()}
    </React.Fragment>
  );
};
